import { ref, watch } from 'vue';
import type { ChatTypeFilter } from '@/store/chat';

// Module-level so the chat panel can be toggled from outside the navbar
// (keyboard shortcut, command palette). Opening has side effects, and they are
// owned by whoever they belong to: user-menu-icons.vue watches this ref to close
// the notifications panel, and chat-panel.vue marks the chat seen.
const isOpen = ref(false);

// The tab lives here rather than inside the panel because a shortcut that opens
// the agent has to know whether the agent is already what is showing — pressing
// it again then means "close", not "switch to the tab I am on".
const tab = ref<ChatTypeFilter>('all');

// Where leaving the agent goes back to. The agent is a pane of its own rather
// than a filter over the feed, so the way out of it is the tab the user was
// last reading, not whichever one happens to be first.
let lastChatTab: ChatTypeFilter = 'all';
// Sync: the next keypress may arrive before a queued watcher would have run,
// and it would then step back to a tab the user has already left.
watch(
  tab,
  (current) => {
    if (current !== 'agent') lastChatTab = current;
  },
  { flush: 'sync' }
);

export const chatPanel = {
  isOpen,
  tab,
  /** Open or close, whatever is showing — the panel's own control in the bar. */
  toggle: () => {
    isOpen.value = !isOpen.value;
  },
  /** Opens on whatever was showing last — the phone menu's twin of the bar icon. */
  open: () => {
    isOpen.value = true;
  },
  /**
   * Asks for the chat specifically: opening, closing, or stepping off the agent.
   * Unlike {@link toggle} this applies while the panel is shut too — closing it
   * from the agent leaves the agent selected, and a plain open would bring back
   * the very pane the user just dismissed.
   */
  toggleChat: () => {
    if (tab.value === 'agent') {
      tab.value = lastChatTab;
      isOpen.value = true;
      return;
    }
    isOpen.value = !isOpen.value;
  },
  /** Opens on a tab, or closes again when that tab is already what is showing. */
  toggleOn: (wanted: ChatTypeFilter) => {
    if (isOpen.value && tab.value === wanted) {
      isOpen.value = false;
      return;
    }
    tab.value = wanted;
    isOpen.value = true;
  },
};
